import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Menu, Phone, X } from "lucide-react";
import { SITE, IMAGES, NAV } from "../data/site";
import { trackEvent } from "../utils/analytics";

export const scrollToId = (to) => {
  const el = document.querySelector(to);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -100 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const go = (to) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(to), 400);
    } else {
      setTimeout(() => scrollToId(to), 60);
    }
  };

  return (
    <header data-testid="site-header" className="fixed top-0 inset-x-0 z-50">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* FLOATING PILL BAR */}
        <div
          className={`mt-3 md:mt-4 flex items-center justify-between rounded-2xl lg:rounded-full border px-4 lg:px-6 transition-all duration-500 ${
            scrolled
              ? "h-14 bg-white/90 backdrop-blur-xl border-slate-200/80 shadow-[0_10px_35px_rgba(7,22,44,0.12)]"
              : "h-16 bg-white/70 backdrop-blur-xl border-slate-200/60 shadow-[0_8px_30px_rgba(7,22,44,0.08)]"
          }`}
        >
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2 shrink-0" aria-label="Se Klart Ut – till startsidan">
            <img src={IMAGES.logo} alt="Se Klart Ut" className={`w-auto object-contain transition-all duration-500 ${scrolled ? "h-8" : "h-10"}`} />
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5" aria-label="Huvudmeny">
            {NAV.map((item) => (
              <a
                key={item.to}
                href={item.to}
                onClick={go(item.to)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="relative px-4 py-2 rounded-full text-sm font-medium text-brand-ink/75 hover:text-brand-blue transition-colors duration-200 after:absolute after:left-4 after:right-4 after:bottom-1 after:h-0.5 after:rounded-full after:bg-brand-blue after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform after:duration-300"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a href={SITE.phoneHref} data-testid="header-phone" className="text-sm font-medium text-brand-ink/75 hover:text-brand-blue transition-colors">
              {SITE.phone}
            </a>
            <Link
              to="/boka-offert"
              data-testid="nav-book-button"
              onClick={() => trackEvent("boka_click", { location: "header_desktop" })}
              className="group inline-flex items-center gap-2 rounded-full bg-brand-blue text-white text-sm font-semibold px-5 py-2.5 hover:bg-brand-blueHover hover:shadow-[0_10px_25px_rgba(14,98,254,0.3)] active:scale-[0.98] transition-all duration-300"
            >
              Boka fönsterputs
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </Link>
          </div>

          {/* MOBILE: phone + quick booking between logo and menu */}
          <div className="flex lg:hidden items-center gap-2 ml-auto mr-2">
            <a
              href={SITE.phoneHref}
              data-testid="mobile-header-phone"
              className="flex items-center gap-1.5 text-xs font-semibold text-brand-ink"
              onClick={() => trackEvent("phone_click", { location: "header_mobile" })}
            >
              <Phone className="h-3.5 w-3.5 text-brand-blue" /> {SITE.phone}
            </a>
            <Link
              to="/boka-offert"
              data-testid="mobile-header-book"
              onClick={() => trackEvent("boka_click", { location: "header_mobile" })}
              className="rounded-full bg-brand-blue text-white text-xs font-semibold px-4 py-2"
            >
              Boka
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden h-10 w-10 rounded-full bg-brand-iceBg border border-slate-200/70 flex items-center justify-center text-brand-ink shrink-0"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Stäng meny" : "Öppna meny"}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* MOBILE DROPDOWN PANEL — lightweight, sits under the bar, page stays visible behind */}
        <AnimatePresence>
          {open && (
            <motion.div
              data-testid="mobile-menu"
              className="lg:hidden absolute left-4 right-4 sm:left-6 sm:right-6 top-full mt-2 origin-top rounded-3xl bg-white/95 backdrop-blur-xl border border-slate-200/70 shadow-[0_25px_60px_-15px_rgba(7,22,44,0.25)] overflow-hidden"
              initial={{ opacity: 0, y: -12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.98 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            >
              <nav className="p-3" aria-label="Mobilmeny">
                {NAV.map((item, i) => (
                  <motion.a
                    key={item.to}
                    href={item.to}
                    onClick={go(item.to)}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group flex items-center justify-between gap-3 rounded-2xl px-4 py-3.5 text-base font-semibold text-brand-ink hover:bg-brand-iceBg transition-colors"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.04, duration: 0.3 }}
                  >
                    {item.label}
                    <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-brand-blue transition-colors" />
                  </motion.a>
                ))}
              </nav>
              <div className="px-3 pb-3">
                <Link
                  to="/boka-offert"
                  data-testid="mobile-book-button"
                  onClick={() => trackEvent("boka_click", { location: "menu_mobile" })}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue text-white font-semibold px-6 py-3.5 hover:bg-brand-blueHover transition-colors"
                >
                  Boka fönsterputs <ArrowRight className="h-4 w-4" />
                </Link>
                <a href={SITE.phoneHref} className="mt-3 mb-1 flex items-center justify-center gap-2 text-slate-500 text-sm">
                  <Phone className="h-4 w-4" /> {SITE.phone}
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
